import { type CsticId, type LinearSubstep, SimpleCsticId } from "~/Configurators/Linear/models";
import LabeledInput from "../../../../common/components/LabeledInput";
import { useBooleanState } from "../../../../common/hooks/useBooleanState";
import { SubstepType } from "~/Configurators/Linear/dtos";
import { IconClickable } from "../../../../common/components/icon/IconClickable";
import { IconType } from "../../../../common/components/icon/IconType";
import { IconSize } from "../../../../common/components/icon/IconSize";
import Tab from "../../../../common/components/Tabs/Tab";

import classNames from "classnames";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "~/common/m3/Dialog/Dialog";
import { Tabs } from "~/common/m3/Tabs/Tabs";
import LanguageAwareExternalContent from "~/Configurators/Linear/components/LanguageAwareExternalContent";
import { CsticNames } from "./constants";
import { SimpleCsticTileField } from "~/Configurators/Shaft/components/SimpleCsticTileField";
import { LinearTileV2 } from "~/Configurators/Shaft/substeps/LinearTileV2";
import { useCsticSelectProps } from "~/Configurators/Shaft/components/useCsticSelectProps";
import { Stack } from "@mui/material";
import { useXmlConfiguratorTranslations } from "~/Configurators/common/useXmlConfiguratorTranslations";
import { KmatType } from "~/Configurators/Linear/getKmatType";
import { CARRIAGE_CONFIGURATOR_PRODUCT_CODE } from "~/Configurators/constants";
import { getProductCode } from "~/Configurators/Linear/redux/selectors";
import { useSelector } from "react-redux";

interface Props {
  className?: string;
  def: LinearSubstep;
}

export const SelectLubricationConnectorPopup = ({ def, className }: Props) => {
  const { val: isModalOpen, setTrue: openModal, setFalse: closeModal } = useBooleanState(false);

  const lubricationConnectorHelper = def.inputCstics[CsticNames.LubricationConnector];
  const lubricationConnectorId = SimpleCsticId(lubricationConnectorHelper.name);

  const productCode = useSelector(getProductCode);
  const isClassicCarriage = productCode === CARRIAGE_CONFIGURATOR_PRODUCT_CODE;

  const connectorTypeHelper = def.helperCstics[CsticNames.LubricationConnectorType];

  const {
    cstic: lubricationConnectorCstic,
    selectProps: { disabled, error },
  } = useCsticSelectProps({
    csticId: lubricationConnectorId,
    substep: SubstepType.ConfigureCarriage,
    required: lubricationConnectorHelper.required,
  });

  const {
    selectProps: { onChange: setActiveTab, value: activeTab },
    csticValues: tabs,
  } = useCsticSelectProps({
    csticId: {
      key: connectorTypeHelper.name,
      type: isClassicCarriage ? KmatType.CARRIAGE : KmatType.DEFAULT,
      index: 0,
    },
    substep: SubstepType.ConfigureCarriage,
  });

  const selectedConnector = lubricationConnectorCstic.value;
  const selectedValue = lubricationConnectorCstic.values.find((val) => val.value === selectedConnector);
  const selectedValueLabel = selectedValue?.label ?? "";

  const label = lubricationConnectorCstic.label || "MISSING LABEL";

  const spanClasses = classNames("scha__lc__external-label", {
    "f-disabled-text": lubricationConnectorHelper.disabled,
  });

  return (
    <>
      <div className={className}>
        <LabeledInput
          label={<LanguageAwareExternalContent content={label} loadedClassname={"scha__lc__external-label"} />}
          hasError={error}
        >
          <div className={"title-medium mt-1 d-flex flex-align-center"}>
            {selectedValue?.standard && <LinearTileV2.StandardIndicator omitText className="icon-margin" />}
            <span className={"icon-margin"}>
              <LanguageAwareExternalContent content={selectedValueLabel} loadedClassname={spanClasses} />
            </span>
            <IconClickable
              type={IconType.Pencil}
              size={IconSize.FONT_20}
              onClick={openModal}
              disabled={lubricationConnectorHelper.disabled}
            />
          </div>
        </LabeledInput>
      </div>
      {isModalOpen && (
        <SelectionModal
          onClose={closeModal}
          title={label}
          description={lubricationConnectorCstic.description}
          activeTab={activeTab ?? ""}
          onTabChange={setActiveTab}
          csticId={lubricationConnectorId}
          tabs={tabs}
        />
      )}
    </>
  );
};

type TabData = {
  value: string;
  label: string;
};

interface SelectionModalProps {
  onClose: () => void;
  title: string;
  description?: string;
  tabs: TabData[];
  activeTab: string;
  onTabChange: (tab: string) => void;
  csticId: CsticId;
}

function SelectionModal({ onClose, title, description, tabs, activeTab, onTabChange, csticId }: SelectionModalProps) {
  const xmlT = useXmlConfiguratorTranslations().t!;
  return (
    <Dialog open={true} onClose={onClose} maxWidth="desktop-m">
      <DialogHeader onClose={onClose}>
        <DialogTitle sx={{ p: 0 }}>
          <LanguageAwareExternalContent content={title} loadedClassname={"scha__lc__external-label"} />
        </DialogTitle>
      </DialogHeader>
      <DialogContent>
        <Stack gap={2}>
          {description && (
            <LanguageAwareExternalContent content={description} loadedClassname={"scha__lc__external-label"} />
          )}
          <Tabs onChange={(e, val) => onTabChange(val)} variant="fullWidth" value={activeTab}>
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                value={tab.value}
                label={
                  <LanguageAwareExternalContent content={tab.label} loadedClassname={"scha__lc__external-label"} />
                }
              />
            ))}
          </Tabs>

          <SimpleCsticTileField
            csticId={csticId}
            tilesPerRow={4}
            substepType={SubstepType.ConfigureCarriage}
            renderTile={({ onClick, disabled, selected, value }) => (
              <LinearTileV2.Base onClick={onClick} selected={selected} disabled={disabled}>
                <LinearTileV2.Image src={xmlT(value.imageUrl)} ratio="" />
                <LinearTileV2.CheckedIndicator />
                <p className="m-0">{value.value}</p>
                {value.standard && <LinearTileV2.StandardIndicator />}
              </LinearTileV2.Base>
            )}
            onClick={onClose}
          />
        </Stack>
        {/* 
        {tiles.length > 0 && (
          <Grid container columns={4} spacing={"var(--gutter)"}>
            {tiles.map((tile, index) => (
              <Grid key={index} size={1}>
                <LinearTile
                  key={tile.value}
                  index={index}
                  isStandard={tile.standard}
                  imageUrl={tile.imageUrl}
                  onClickHandler={() => {
                    onTileClick(tile);
                  }}
                  info={tile.info}
                  isPicked={tile.value === activeTile}
                  title={tile.label}
                />
              </Grid>
            ))}
          </Grid>
        )} */}
      </DialogContent>
    </Dialog>
  );
}
